import api from "@/lib/axiosConfig.ts";
import {
  Artwork,
  ArtworkBasic,
  ArtworkCoreInfo,
  Ensemble,
  FetchedArtwork,
  TinyArtwork,
} from "@/lib/types";
import { parseJsonOrUndefined } from "@/lib/utils";
import {
  keepPreviousData,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { SortingState } from "@tanstack/react-table";
import {
  BriefSellable,
  PaginatedReturn,
  PagingParameters,
  RelativePosition,
  SellableItemType,
  sortingToUrlParam,
} from "../dtos";
import { AppFile } from "../file";

export interface ArtworksDTO {
  where: any;
  order: object[];
  take: number;
  skip: number;
}

export const baseRoute = "back/api/artworks";

export const mapFetchedArtworks = (artwork: FetchedArtwork): Artwork => ({
  ...artwork,
  //TECH_DEBT
  oeuvresDescriptionDimensionsOeuvre: parseJsonOrUndefined(
    artwork.oeuvresDescriptionDimensionsOeuvre,
  ),
  oeuvresRegieDimensionsCadre: parseJsonOrUndefined(
    artwork.oeuvresRegieDimensionsCadre,
  ),
  oeuvresRegieDimensionsCaisse: parseJsonOrUndefined(
    artwork.oeuvresRegieDimensionsCaisse,
  ),
  oeuvresDescriptionDimensionsHorsMarges: parseJsonOrUndefined(
    artwork.oeuvresDescriptionDimensionsHorsMarges,
  ),
});

export async function getArtworks({
  where,
  order,
  take,
  skip,
}: ArtworksDTO): Promise<Artwork[]> {
  const { data } = await api.post<FetchedArtwork[]>(
    `back/api/artworks?take=${take || 10}&skip=${skip || 0}`,
    {
      where,
      orderBy: order,
    },
  );
  localStorage.setItem(
    "artworksDashboard",
    JSON.stringify({ where, take, skip, order }),
  );
  return data.map(mapFetchedArtworks);
}

export const useGetAllArtworks = ({
  where,
  order,
  take,
  skip,
}: ArtworksDTO) => {
  return useQuery({
    queryKey: ["artworksDashboard", where, order, take, skip],
    queryFn: () => getArtworks({ where, order, take, skip }),
    placeholderData: keepPreviousData,
  });
};

async function getArtworkById(
  id: string | undefined,
  needRelation = true,
): Promise<Artwork> {
  if (!id) {
    throw new Error("Id is required");
  }
  const { data } = await api.get<FetchedArtwork>(
    `back/api/artworks/${id}?needRelation=${needRelation}`,
  );

  return mapFetchedArtworks(data);
}

export const useGetArtworkById = (
  id: string | undefined,
  needRelation = true,
) => {
  return useQuery({
    queryKey: ["artworks", id],
    queryFn: () => getArtworkById(id, needRelation),
    enabled: !!id,
  });
};

async function getRelatedFieldsWithAttachments(id: string | undefined) {
  if (!id) {
    throw new Error("Id is required");
  }
  const { data } = await api.get(`back/api/artworks/related/${id}`);
  return data;
}

export const useGetRelatedFieldsWithAttachments = (id: string | undefined) => {
  return useQuery({
    queryKey: ["artworkRelated", id],
    queryFn: () => getRelatedFieldsWithAttachments(id),
    enabled: !!id,
  });
};

async function updateArtwork(
  data: Artwork & {
    permanentEnsemblesIds?: string[];
    relatedAuthorIds?: string[];
  },
) {
  let dataToSend: any = { ...data };

  if ("selectedInCmsId" in dataToSend) {
    delete (dataToSend as any).selectedInCmsId;
  }

  if ("instanceId" in dataToSend) {
    delete (dataToSend as any).instanceId;
  }
  if ("oeuvresLocalisationHistoriqueCollectionId" in dataToSend) {
    const colId = dataToSend.oeuvresLocalisationHistoriqueCollectionId;
    delete (dataToSend as any).oeuvresLocalisationHistoriqueCollectionId;
    dataToSend = {
      ...dataToSend,
      oeuvresLocalisationHistoriqueCollection:
        colId == null ? { disconnect: true } : { connect: { id: colId } },
    };
  }
  if ("selectionPageId" in dataToSend) {
    delete (dataToSend as any).selectionPageId;
  }
  if ("updatedById" in dataToSend) {
    delete (dataToSend as any).updatedById;
  }
  if ("createdById" in dataToSend) {
    delete (dataToSend as any).createdById;
  }
  if ("relatedAuthor" in dataToSend) {
    delete (dataToSend as any).relatedAuthor;
  }

  const { data: result } = await api.put<any, any, { data: ArtworkBasic }>(
    `back/api/artworks/${data.id}`,
    {
      data: {
        ...dataToSend,
        oeuvresRegieDimensionsCadre: JSON.stringify(
          dataToSend.oeuvresRegieDimensionsCadre,
        ),
        oeuvresRegieDimensionsCaisse: JSON.stringify(
          dataToSend.oeuvresRegieDimensionsCaisse,
        ),
        oeuvresDescriptionDimensionsOeuvre: JSON.stringify(
          dataToSend.oeuvresDescriptionDimensionsOeuvre,
        ),
        oeuvresDescriptionDimensionsHorsMarges: JSON.stringify(
          dataToSend.oeuvresDescriptionDimensionsHorsMarges,
        ),
      },
    },
  );
  return result;
}

export const useUpdateArtwork = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateArtwork,
    onSuccess: (_, v) => {
      queryClient.invalidateQueries({ queryKey: ["artworks", v.id] });
    },
  });
};

export async function getCountByStatus(prismaQuery: any | undefined) {
  const { data } = await api.post(`back/api/artworks/countByStatus`, {
    data: prismaQuery ?? {},
  });
  return data;
}

export const getRelativePosition = async (
  id: string,
  order: object,
  where: object,
) => {
  const { data } = await api.post<RelativePosition>(
    `${baseRoute}/${id}/position`,
    {
      order,
      where,
    },
  );
  return data;
};

export const useCountByStatus = (prismaQuery: any | undefined) => {
  return useQuery({
    queryKey: ["countByStatus", "artworks", prismaQuery],
    queryFn: () => getCountByStatus(prismaQuery),
  });
};

async function getSellables(
  type: SellableItemType,
  paging?: PagingParameters,
  sorting: SortingState = [],
  search?: string,
) {
  // if search is empty, then it's null
  const safeSearch = (search ?? "").trim();

  const { data } = await api.get<PaginatedReturn<BriefSellable>>(
    "back/api/articles/sellable",
    {
      params: {
        type,
        ...(paging ?? { page: 0, pageSize: 50 }),
        ...(sorting.length !== 0 ? { sort: sortingToUrlParam(sorting) } : null),
        ...(search !== "" ? { search: safeSearch } : null),
      },
    },
  );
  return data;
}

export const useGetSellables = (
  itemType: SellableItemType,
  paging?: PagingParameters,
  sorting?: SortingState,
  search?: string,
) => {
  return useQuery({
    queryKey: ["sellables", itemType, paging, sorting, search],
    queryFn: () => getSellables(itemType, paging, sorting, search),
    placeholderData: keepPreviousData,
  });
};

async function getRelatedArtworks(id: string | undefined): Promise<Artwork[]> {
  if (!id) {
    throw new Error("Id is required");
  }
  const { data } = await api.get(`back/api/artworks/${id}/relatedArtworks`);
  return data;
}

export const useGetRelatedArtworks = (
  id: string | undefined,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: ["artworks", "relatedArtworks", id],
    queryFn: () => getRelatedArtworks(id),
    enabled: !!id && enabled,
  });
};

async function createRelatedArtworks(ids: string[]) {
  const { data } = await api.post(`back/api/artworks/create/relatedArtworks`, {
    ids,
  });
  return data;
}

export const useCreateRelatedArtworks = (id: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createRelatedArtworks,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["artworks", "relatedArtworks", id],
      });
    },
  });
};

async function countAllRelatedFields(id: string | undefined) {
  if (!id) {
    throw new Error("Id is required");
  }
  const { data } = await api.get(`back/api/artworks/${id}/related/count`);
  return data;
}

export const useCountAllRelatedFields = (id: string | undefined) => {
  return useQuery({
    queryKey: ["artworks", "relatedCount", id],
    queryFn: () => countAllRelatedFields(id),
    enabled: !!id,
  });
};

async function getRelatedAudiovisuals(id: string | undefined) {
  if (!id) {
    throw new Error("Id is required");
  }
  const { data } = await api.get(`back/api/artworks/${id}/relatedAudiovisuals`);
  return data;
}

export const useGetRelatedAudiovisuals = (
  id: string | undefined,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: ["artworks", "relatedAudiovisuals", id],
    queryFn: () => getRelatedAudiovisuals(id),
    enabled: !!id && enabled,
  });
};

async function getRelatedDocuments(id: string | undefined) {
  if (!id) {
    throw new Error("Id is required");
  }
  const { data } = await api.get(`back/api/artworks/${id}/relatedDocuments`);
  return data;
}

export const useGetRelatedDocuments = (
  id: string | undefined,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: ["artworks", "relatedDocuments", id],
    queryFn: () => getRelatedDocuments(id),
    enabled: !!id && enabled,
  });
};

async function getRelatedWritings(id: string | undefined) {
  if (!id) {
    throw new Error("Id is required");
  }
  const { data } = await api.get(`back/api/artworks/${id}/relatedWritings`);
  return data;
}

export const useGetRelatedWritings = (
  id: string | undefined,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: ["artworks", "relatedWritings", id],
    queryFn: () => getRelatedWritings(id),
    enabled: !!id && enabled,
  });
};

async function getRelatedPhotographies(id: string | undefined) {
  if (!id) {
    throw new Error("Id is required");
  }
  const { data } = await api.get(
    `back/api/artworks/${id}/relatedPhotographies`,
  );
  return data;
}

export const useGetRelatedPhotographies = (
  id: string | undefined,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: ["artworks", "relatedPhotographies", id],
    queryFn: () => getRelatedPhotographies(id),
    enabled: !!id && enabled,
  });
};

async function getRelatedMultipleprinters(id: string | undefined) {
  if (!id) {
    throw new Error("Id is required");
  }
  const { data } = await api.get(
    `back/api/artworks/${id}/relatedMultipleprinters`,
  );
  return data;
}

export const useGetRelatedMultipleprinters = (
  id: string | undefined,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: ["artworks", "relatedMultipleprinters", id],
    queryFn: () => getRelatedMultipleprinters(id),
    enabled: !!id && enabled,
  });
};

async function createRelatedMultipleprinters(ids: string[]) {
  const { data } = await api.post(
    `back/api/artworks/create/relatedMultipleprinters`,
    { ids },
  );
  return data;
}

export const useCreateRelatedMultipleprinters = (id: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createRelatedMultipleprinters,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["artworks", "relatedMultipleprinters", id],
      });
    },
  });
};

async function createRelatedPhotographies(ids: string[]) {
  const { data } = await api.post(
    `back/api/artworks/create/relatedPhotographies`,
    { ids },
  );
  return data;
}

export const useCreateRelatedPhotographies = (id: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createRelatedPhotographies,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["artworks", "relatedPhotographies", id],
      });
    },
  });
};

async function createRelatedDocuments(ids: string[]) {
  const { data } = await api.post(`back/api/artworks/create/relatedDocuments`, {
    ids,
  });
  return data;
}

export const useCreateRelatedDocuments = (id: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createRelatedDocuments,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["artworks", "relatedDocuments", id],
      });
    },
  });
};

async function createRelatedAudiovisuals(ids: string[]) {
  const { data } = await api.post(
    `back/api/artworks/create/relatedAudiovisuals`,
    { ids },
  );
  return data;
}

export const useCreateRelatedAudiovisuals = (id: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createRelatedAudiovisuals,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["artworks", "relatedAudiovisuals", id],
      });
    },
  });
};

async function createRelatedWritings(ids: string[]) {
  const { data } = await api.post(`back/api/artworks/create/relatedWritings`, {
    ids,
  });
  return data;
}

export const useCreateRelatedWritings = (id: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createRelatedWritings,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["artworks", "relatedWritings", id],
      });
    },
  });
};

interface DeleteRelationDTO {
  relation: string;
  relatedId: string;
  id: string;
}

async function deleteRelation({ relation, relatedId, id }: DeleteRelationDTO) {
  const { data } = await api.delete(
    `back/api/artworks/delete/${relation}/${relatedId}/${id}`,
  );
  return data;
}

export const useDeleteRelation = (id: string, relationQueryKey: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteRelation,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["artworks", relationQueryKey, id],
      });
    },
  });
};

async function deleteArtworkInExhibition(id: string) {
  const { data } = await api.delete(
    `back/api/artworks/artwork-in-exhibition/${id}`,
  );
  return data;
}

export const useDeleteArtworkInExhibition = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteArtworkInExhibition,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "artworks",
          "complexRelationFields",
          id,
          "artworkInExhibitions",
        ],
      });
    },
  });
};

async function deleteArtworkInBibliography(id: string) {
  const { data } = await api.delete(
    `back/api/artworks/artwork-in-bibliography/${id}`,
  );
  return data;
}

export const useDeleteArtworkInBibliography = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteArtworkInBibliography,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "artworks",
          "complexRelationFields",
          id,
          "artworkInBibliography",
        ],
      });
    },
  });
};

interface CreateOrUpdateArtworkInAuctionDTO {
  id: string;
  oeuvresDansVentesDescriptionNumeroLot?: string;
  oeuvresDansVentesDescriptionReproduitPage?: string;
  oeuvresDansVentesDescriptionEstimationHaute?: string;
  oeuvresDansVentesDescriptionEstimationBasse?: string;
  oeuvresDansVentesDescriptionVendu?: boolean;
  oeuvresDansVentesDescriptionResultatNonListe?: boolean;
  oeuvresDansVentesDescriptionNonVendu?: boolean;
  oeuvresDansVentesDescriptionNonVenu?: boolean;
  oeuvresDansVentesDescriptionNonReproduit?: boolean;
  oeuvresDansVentesDescriptionRetire?: boolean;
  oeuvresDansVentesDescriptionMiseAPrix?: string;
  oeuvresDansVentesDescriptionPrixMarteau?: string;
  oeuvresDansVentesDescriptionPrixAvecFrais?: string;
  oeuvresDansVentesDescriptionNotes?: string;
  artworkId: string;
  auctionId: string;
}

async function createOrUpdateArtworkInAuction(
  data: CreateOrUpdateArtworkInAuctionDTO,
) {
  const { data: result } = await api.post(
    `back/api/artworks/artwork-in-auction`,
    { data },
  );
  return result;
}

export const useCreateOrUpdateArtworkInAuction = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createOrUpdateArtworkInAuction,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "artworks",
          "complexRelationFields",
          id,
          "artworkInAuctions",
        ],
      });
    },
  });
};

async function deleteArtworkInAuction(id: string) {
  const { data } = await api.delete(
    `back/api/artworks/artwork-in-auction/${id}`,
  );
  return data;
}

export const useDeleteArtworkInAuction = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteArtworkInAuction,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "artworks",
          "complexRelationFields",
          id,
          "artworkInAuctions",
        ],
      });
    },
  });
};

type CreateArtworkParams = {
  oeuvresDescriptionNumeroEnregistrement: string;
};

const createArtwork = async ({
  oeuvresDescriptionNumeroEnregistrement,
}: CreateArtworkParams) => {
  return await api
    .post<Artwork>("back/api/artworks/create", {
      oeuvresDescriptionNumeroEnregistrement,
    })
    .then((res) => res.data);
};

export const useCreateArtwork = (
  options?: UseMutationOptions<Artwork, Error, CreateArtworkParams>,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: createArtwork,
    onSuccess: (data, vars, ctx) => {
      queryClient.invalidateQueries({ queryKey: ["artworksDashboard"] });
      queryClient.invalidateQueries({ queryKey: ["artworksDashboard"] });
      queryClient.invalidateQueries({ queryKey: ["countByStatus", "artwork"] });
      queryClient.invalidateQueries({ queryKey: ["instance", "count-data"] });
      options?.onSuccess?.(data, vars, ctx);
    },
  });
};

export const getRelatedAdministrativeDocuments = async (artworkId: string) => {
  const { data } = await api.get<AppFile[]>(
    `${baseRoute}/${artworkId}/administrative-documents`,
  );
  return data;
};

export const useGetRelatedAdministrativeDocuments = (artworkId: string) => {
  return useQuery({
    queryKey: ["artworks", artworkId, "administrative-documents"],
    queryFn: () => getRelatedAdministrativeDocuments(artworkId),
  });
};

const getPermanentEnsembles = async (artworkId: string) => {
  const { data } = await api.get<Ensemble[]>(
    `${baseRoute}/${artworkId}/ensembles/permanent`,
  );
  return data;
};

export const useGetPermanentEnsemblesRelatedToArtwork = (artworkId: string) => {
  return useQuery({
    queryKey: ["artworks", artworkId, "permanent-ensembles"],
    queryFn: () => getPermanentEnsembles(artworkId),
  });
};

export const getAllIds = async (where?: object) => {
  const { data } = await api.post<{ id: string }[]>(`${baseRoute}/ids`, {
    where,
  });
  return data;
};

export const getTinyArtworks = async (
  query: Partial<{
    where: object;
    orderBy: object;
    take: number;
    skip: number;
  }>,
) => {
  const { data } = await api.post<TinyArtwork[]>(`${baseRoute}/tiny`, query);
  return data;
};

export const useGetTinyArtworks = (
  query: Partial<{
    where: object;
    orderBy: object;
    take: number;
    skip: number;
  }>,
) =>
  useQuery({
    queryKey: ["artworks", query, "tiny"],
    queryFn: () => getTinyArtworks(query),
  });

export async function validateNoDuplicateRecordingNumbers(
  recordingNumber: string,
) {
  await api.get<void>(
    `${baseRoute}/registration-number/validate/no-duplicates`,
    {
      params: {
        value: recordingNumber,
      },
    },
  );
}

export const getAllArtworksCoreInfo = async (
  page: PagingParameters,
  search?: string,
) => {
  const response = await api.get<PaginatedReturn<ArtworkCoreInfo>>(
    baseRoute + "/core-info",
    {
      params: {
        ...page,
        search,
      },
    },
  );
  return response.data;
};
export const useGetAllArtworksCoreInfo = (
  page: PagingParameters,
  search?: string,
) => {
  return useQuery({
    queryKey: ["artworks", "core-info", page, search],
    queryFn: () => getAllArtworksCoreInfo(page, search),
    placeholderData: keepPreviousData,
  });
};
